<div class="web-container">
  <div class="web-header">
    <div class="web-heading">
      <span class="material-icons web-heading-icon">travel_explore</span>
      <div>
        <h2>Website Knowledge Crawler</h2>
        <p>Crawl a website and review the extracted page content for chatbot knowledge preparation.</p>
      </div>
    </div>
  </div>

  <div class="web-grid">
    <section class="web-card web-upload-card">
      <div class="web-card-title">
        <span class="material-icons">add_link</span>
        Start a Crawl
      </div>

      <label class="web-field">
        <span>Website address</span>
        <input
          class="web-url-input-single"
          type="url"
          [(ngModel)]="baseUrl"
          name="baseUrl"
          placeholder="https://transworld-home.com/" />
      </label>

      <div class="web-field-row">
        <label class="web-field">
          <span>Maximum pages</span>
          <input type="number" min="1" max="100000" [(ngModel)]="maxPages" name="maxPages" />
        </label>
        <label class="web-field">
          <span>Maximum depth</span>
          <input type="number" min="0" max="100" [(ngModel)]="maxDepth" name="maxDepth" />
        </label>
        <label class="web-field">
          <span>Concurrency</span>
          <input type="number" min="1" max="32" [(ngModel)]="concurrency" name="concurrency" />
        </label>
      </div>

      <label class="web-field">
        <span>Scope</span>
        <select [(ngModel)]="scopeMode" name="scopeMode">
          <option value="EXACT_ORIGIN">Same origin</option>
          <option value="BASE_PATH">Base path only</option>
          <option value="INCLUDE_SUBDOMAINS">Include subdomains</option>
        </select>
      </label>

      <div class="web-toggles">
        <label><input type="checkbox" [(ngModel)]="respectRobotsTxt" name="respectRobotsTxt" /> Respect robots.txt</label>
        <label><input type="checkbox" [(ngModel)]="saveRenderedHtml" name="saveRenderedHtml" /> Save rendered HTML</label>
        <label><input type="checkbox" [(ngModel)]="saveScreenshot" name="saveScreenshot" /> Save screenshot</label>
      </div>

      <button type="button" class="web-link-btn" (click)="showAdvanced = !showAdvanced">
        {{ showAdvanced ? 'Hide' : 'Show' }} advanced settings
      </button>

      <div class="web-field-row" *ngIf="showAdvanced">
        <label class="web-field">
          <span>Navigation timeout (ms)</span>
          <input type="number" min="1000" [(ngModel)]="navigationTimeoutMs" name="navigationTimeoutMs" />
        </label>
        <label class="web-field">
          <span>Wait after load (ms)</span>
          <input type="number" min="0" max="60000" [(ngModel)]="waitAfterLoadMs" name="waitAfterLoadMs" />
        </label>
      </div>

      <div *ngIf="showAdvanced">
        <div class="web-toggles">
          <label><input type="checkbox" [(ngModel)]="interactWithCarousels" name="interactWithCarousels" /> Interact with sliders</label>
        </div>
        <div class="web-field-row" *ngIf="interactWithCarousels">
          <label class="web-field">
            <span>Maximum slides per carousel</span>
            <input type="number" min="1" max="100" [(ngModel)]="maxSlidesPerCarousel" name="maxSlidesPerCarousel" />
          </label>
          <label class="web-field">
            <span>Slide transition wait (ms)</span>
            <input type="number" min="0" max="10000" [(ngModel)]="slideTransitionWaitMs" name="slideTransitionWaitMs" />
          </label>
        </div>
      </div>

      <div class="web-upload-actions">
        <span class="web-muted">Crawls continue on the server; results appear below as they finish.</span>
        <button class="web-btn web-btn-primary" (click)="startCrawl()" [disabled]="starting">
          <span class="material-icons">travel_explore</span>
          {{ starting ? 'Starting...' : 'Start Crawl' }}
        </button>
      </div>
    </section>

    <section class="web-card web-result-card" *ngIf="selectedJob as job">
      <div class="web-card-title web-card-title-between">
        <span>
          <span class="material-icons">insights</span>
          Crawl Progress
        </span>
        <span class="web-status" [ngClass]="statusClass(job.status)">{{ job.status }}</span>
      </div>

      <div class="web-result-url">{{ job.baseUrl }}</div>
      <div class="web-muted web-crawl-id">Crawl ID: {{ job.crawlId }}</div>

      <p class="web-error" *ngIf="job.error">{{ job.error }}</p>

      <div class="web-loading" *ngIf="isActive(job.status)">
        Crawl {{ job.status | lowercase }}. Live counters update automatically.
      </div>

      <div class="web-muted" *ngIf="job.detail?.currentUrl">
        Current URL: {{ job.detail?.currentUrl }}
      </div>

      <div class="web-metrics">
        <span>Discovered: <b>{{ formatCount(job.detail?.discoveredCount) }}</b></span>
        <span>Queued: <b>{{ formatCount(job.detail?.queuedCount) }}</b></span>
        <span>Processed: <b>{{ formatCount(job.detail?.processedCount) }}</b></span>
        <span>Successful: <b>{{ formatCount(job.detail?.successfulCount) }}</b></span>
        <span>Failed: <b>{{ formatCount(job.detail?.failedCount) }}</b></span>
        <span>Skipped: <b>{{ formatCount(job.detail?.skippedCount) }}</b></span>
        <span>Duplicates: <b>{{ formatCount(job.detail?.duplicateCount) }}</b></span>
        <span>Documents: <b>{{ formatCount(job.detail?.documentCount) }}</b></span>
        <span>Parsed Documents: <b>{{ parsedDocumentCount(job) }}</b></span>
        <span>OCR Read: <b>{{ ocrReadCount(job) }}</b></span>
      </div>

      <div class="web-counter-breakdowns">
        <div class="web-counter-card" *ngIf="skippedRows(job) as rows">
          <ng-container *ngIf="hasCounterRows(rows)">
            <div class="web-counter-title">
              Skipped <b>{{ formatCount(job.detail?.skippedCount) }}</b>
            </div>
            <div class="web-counter-row" *ngFor="let row of rows">
              <span>{{ row.label }}</span>
              <b>{{ row.value }}</b>
            </div>
          </ng-container>
        </div>
        <div class="web-counter-card" *ngIf="duplicateRows(job) as rows">
          <ng-container *ngIf="hasCounterRows(rows)">
            <div class="web-counter-title">
              Duplicates <b>{{ formatCount(job.detail?.duplicateCount) }}</b>
            </div>
            <div class="web-counter-row" *ngFor="let row of rows">
              <span>{{ row.label }}</span>
              <b>{{ row.value }}</b>
            </div>
          </ng-container>
        </div>
        <div class="web-counter-card" *ngIf="ocrRows(job) as rows">
          <ng-container *ngIf="hasCounterRows(rows)">
            <div class="web-counter-title">Image OCR</div>
            <div class="web-counter-row" *ngFor="let row of rows">
              <span>{{ row.label }}</span>
              <b>{{ row.value }}</b>
            </div>
          </ng-container>
        </div>
      </div>

      <div class="web-metrics">
        <span>Started: <b>{{ formatDate(job.detail?.startedAt || job.createdAt) }}</b></span>
        <span>Completed: <b>{{ formatDate(job.detail?.completedAt) }}</b></span>
      </div>

      <div class="web-section" *ngIf="selectedPage as page">
        <div class="web-card-title web-card-title-between">
          <span>Extracted page</span>
          <button class="web-icon-btn" (click)="clearPage()" title="Close page">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="web-result-url">{{ page.final_url }}</div>
        <h3 class="web-result-title">{{ page.title || 'Untitled page' }}</h3>
        <p class="web-result-description" *ngIf="page.meta_description">{{ page.meta_description }}</p>

        <div class="web-section" *ngIf="page.links?.length">
          <h4>Extracted Links</h4>
          <div class="web-faq" *ngFor="let link of page.links">
            <b>{{ link.text || '(link without visible text)' }}</b>
            <p><a [href]="link.url" target="_blank" rel="noopener noreferrer">{{ link.url }}</a></p>
            <small>{{ link.link_type }}</small>
          </div>
        </div>

        <div class="web-section" *ngIf="page.faqs?.length">
          <h4>Detected FAQs</h4>
          <div class="web-faq" *ngFor="let faq of page.faqs">
            <b>{{ faq.question }}</b>
            <p>{{ faq.answer }}</p>
          </div>
        </div>

        <div class="web-section" *ngIf="page.clean_text">
          <h4>Extracted Text</h4>
          <pre>{{ page.clean_text }}</pre>
        </div>
      </div>
    </section>
  </div>

  <section class="web-card web-list-card">
    <div class="web-list-toolbar">
      <div>
        <h3>Crawl Jobs</h3>
        <p>{{ loadingHistory ? 'Loading crawl history...' : (jobs.length + ' saved crawl' + (jobs.length === 1 ? '' : 's')) }}</p>
      </div>
    </div>

    <div class="web-table-wrap">
      <table class="web-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Website</th>
            <th>Status</th>
            <th>Pages</th>
            <th>Started</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngIf="jobs.length === 0">
            <td colspan="6" class="web-empty">No crawls started yet.</td>
          </tr>
          <tr *ngFor="let job of jobs; let i = index" [class.web-selected]="job.crawlId === selectedCrawlId">
            <td>{{ i + 1 }}</td>
            <td class="web-url-cell">
              <a [href]="job.baseUrl" target="_blank" rel="noopener">{{ job.baseUrl }}</a>
            </td>
            <td><span class="web-status" [ngClass]="statusClass(job.status)">{{ job.status }}</span></td>
            <td>{{ formatCount(job.detail?.successfulCount) }}</td>
            <td>{{ formatDate(job.createdAt) }}</td>
            <td>
              <button class="web-btn web-btn-small" (click)="selectJob(job)">
                <span class="material-icons">visibility</span>
                View
              </button>
              <button class="web-btn web-btn-small" (click)="recrawl(job)"
                      [disabled]="isActive(job.status) || actionCrawlId !== null" title="Crawl again with the same settings">
                <span class="material-icons">refresh</span>
                Recrawl
              </button>
              <button class="web-btn web-btn-small" (click)="deleteCrawl(job)"
                      [disabled]="isActive(job.status) || actionCrawlId !== null" title="Delete crawl and extracted content">
                <span class="material-icons">delete</span>
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="web-card web-list-card web-jsonl-card" aria-labelledby="jsonl-exports-heading">
    <div class="web-list-toolbar web-jsonl-toolbar">
      <div>
        <h3 id="jsonl-exports-heading">Website Training</h3>
        <p>
          Train completed website crawls into the independent website knowledge tables.
          Previous training chunks for the same website are replaced automatically.
        </p>
        <p class="web-muted">
          The importer uses extracted page titles, headings, sections, FAQs, tables, and clean text.
        </p>
      </div>
      <div class="web-jsonl-actions">
        <label class="web-field web-jsonl-filter">
          <span>Show crawls</span>
          <select [(ngModel)]="trainingFilter" name="trainingFilter" aria-label="Filter website crawls">
            <option value="COMPLETED">Completed</option>
            <option value="ALL">All</option>
            <option value="ACTIVE">Running</option>
            <option value="FAILED">Failed or interrupted</option>
          </select>
        </label>
        <button
          type="button"
          class="web-btn web-btn-primary web-train-btn"
          (click)="trainCompletedCrawls()"
          [disabled]="!canTrainCompletedCrawls()"
          [title]="availableTrainingRows().length ? 'Train all completed crawls into website knowledge tables.' : 'No completed crawls are ready for training yet.'">
          <span class="material-icons">{{ training ? 'hourglass_top' : 'model_training' }}</span>
          {{ training ? 'Training...' : 'Train Completed Crawls' }}
        </button>
        <div class="web-muted web-training-progress" *ngIf="trainingProgress">{{ trainingProgress }}</div>
      </div>
    </div>

    <div class="web-table-wrap">
      <table class="web-table web-jsonl-table">
        <thead>
          <tr>
            <th>Website</th>
            <th>Crawl ID</th>
            <th>Status</th>
            <th>Page Records</th>
            <th>Completed At</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngIf="jobs.length === 0">
            <td colspan="6" class="web-empty">No website crawls are available yet.</td>
          </tr>
          <tr *ngIf="jobs.length > 0 && trainingRows.length === 0">
            <td colspan="6" class="web-empty">No matching website crawls found.</td>
          </tr>
          <tr *ngFor="let job of trainingRows">
            <td class="web-url-cell">
              <a [href]="job.baseUrl" target="_blank" rel="noopener">{{ job.baseUrl }}</a>
            </td>
            <td class="web-crawl-id-cell">{{ job.crawlId }}</td>
            <td>
              <span class="web-status" [ngClass]="statusClass(job.status)">{{ job.status }}</span>
              <div class="web-muted web-jsonl-status">{{ trainingStatusText(job) }}</div>
            </td>
            <td>{{ job.status === 'COMPLETED' ? trainingRecordCount(job) : '-' }}</td>
            <td>{{ formatDate(job.detail?.completedAt) }}</td>
            <td>
              <button
                type="button"
                class="web-btn web-btn-download"
                (click)="downloadDocuments(job)"
                [disabled]="!canDownloadDocuments(job)"
                [title]="job.status === 'COMPLETED' ? 'Download extracted crawl documents as JSON.' : 'Complete the crawl first.'">
                <span class="material-icons">{{ downloadingCrawlId === job.crawlId ? 'hourglass_top' : 'download' }}</span>
                {{ downloadingCrawlId === job.crawlId ? 'Downloading...' : 'Download Documents' }}
              </button>
              <button
                type="button"
                class="web-btn web-btn-primary"
                (click)="createQaList(job)"
                [disabled]="job.status !== 'COMPLETED' || qaGeneratingCrawlId !== null"
                title="Ask Gemma-QAT to create at least 100 grounded question-answer pairs from this crawl">
                <span class="material-icons">{{ qaGeneratingCrawlId === job.crawlId ? 'hourglass_top' : 'quiz' }}</span>
                {{ qaGeneratingCrawlId === job.crawlId ? 'Gemma is creating QA...' : 'Create QA with Gemma' }}
              </button>
              <button
                *ngIf="qaLists.get(job.crawlId) as qaList"
                type="button"
                class="web-btn web-btn-download"
                (click)="downloadQaList(job)"
                [disabled]="qaDownloadingCrawlId !== null"
                [title]="'Download ' + qaList.qaCount + ' QA pairs as an Excel-compatible CSV file'">
                <span class="material-icons">download</span>
                {{ qaDownloadingCrawlId === job.crawlId ? 'Downloading...' : 'Download QA CSV (' + qaList.qaCount + ')' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="web-card web-list-card" *ngIf="pages.length">
    <div class="web-list-toolbar">
      <div>
        <h3>Extracted Pages</h3>
        <p>{{ pages.length }} page{{ pages.length === 1 ? '' : 's' }} extracted</p>
      </div>
    </div>

    <div class="web-table-wrap">
      <table class="web-table">
        <thead>
          <tr>
            <th>#</th>
            <th>URL</th>
            <th>Title</th>
            <th>Depth</th>
            <th>HTTP</th>
            <th>FAQs</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let page of pages">
            <td>{{ page.sequence_number }}</td>
            <td class="web-url-cell">
              <a [href]="page.final_url" target="_blank" rel="noopener">{{ page.final_url }}</a>
            </td>
            <td>{{ page.title || '-' }}</td>
            <td>{{ page.depth }}</td>
            <td>{{ page.http_status ?? '-' }}</td>
            <td>{{ faqCount(page) }}</td>
            <td>
              <button class="web-btn web-btn-small" (click)="openPage(page)">
                <span class="material-icons">article</span>
                Content
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <div class="web-toasts">
    <div *ngFor="let toast of toasts" class="web-toast" [ngClass]="toast.type">
      {{ toast.message }}
    </div>
  </div>
</div>
